import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Play, Trash2, Send, Route } from 'lucide-react';
import api from '../api';
import DraggableModal from '../components/DraggableModal';
import StatusBadge from '../components/StatusBadge';
import ActionButton from '../components/ActionButton';
import ErrorAlert from '../components/ErrorAlert';

// ── Status colours for pipeline_status ─────────────────────────────────────
const PIPELINE_COLORS = {
  queued: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  draft_ready_for_review: 'bg-green-100 text-green-800',
  human_assisted_custom: 'bg-purple-100 text-purple-800',
  needs_human_from_scratch: 'bg-red-100 text-red-800',
  failed: 'bg-red-100 text-red-800',
};

function PipelineBadge({ status }) {
  if (!status) return null;
  const cls = PIPELINE_COLORS[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-CA');
}

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 py-1 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 w-36 flex-shrink-0">{label}</span>
      <span className="text-xs text-gray-800">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
    </div>
  );
}

function labelize(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function rowFallbackCopy(row) {
  if (!row.row_tag_fallback_applied) return '';
  const requested = labelize(row.requested_tag_value || row.desired_tag_value);
  const actual = row.tag?.label || labelize(row.tag?.value);
  const reason = labelize(row.row_tag_fallback_reason || row.degradation_reason);
  return `Requested ${requested}; showing ${actual}${reason ? ` because ${reason}` : ''}.`;
}

// ── Lead Modal ──────────────────────────────────────────────────────────────
function LeadPlanPreview({ result }) {
  if (!result) return null;
  const preview = result.composer_preview || {};
  const day = result.plan || preview.day || {};
  const theme = preview.theme || {};
  const tripTheme = preview.trip_theme || {};
  const cards = day.cards || [];
  const rowCards = day.row_selected_cards || [];
  const slots = day.slots || [];
  const rowCount = slots.reduce((sum, slot) => sum + ((slot.rows || []).length || 0), 0);
  return (
    <section className="rounded-lg border border-blue-100 bg-blue-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Composer plan</p>
          <p className="mt-1 text-sm font-medium text-blue-950">
            {preview.status || 'preview ready'} · {tripTheme.label || theme.label || theme.theme_id || 'theme pending'}
          </p>
        </div>
        <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-blue-700">
          {(rowCards.length || cards.length)} selected · {rowCount || slots.length} rows
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {(rowCards.length ? rowCards : cards).map(card => (
          <div key={card.candidate_id} className="rounded-md bg-white px-3 py-2">
            <p className="text-sm font-medium text-gray-900">{card.card_title}</p>
            <p className="text-xs text-gray-500">{card.bucket} · {(card.daypart_slots || []).join(', ') || 'no daypart signal'}</p>
          </div>
        ))}
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-semibold text-blue-700">Slot candidates</summary>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {slots.map(slot => (
            <div key={slot.slot_id} className="rounded-md bg-white px-3 py-2 text-xs text-gray-700">
              <p className="font-semibold text-gray-900">{slot.slot_label || slot.daypart}</p>
              <p>{(slot.rows || []).length || 0} rows · {slot.candidate_count || 0} shown from {slot.available_candidate_count || 0}</p>
              {slot.degradation_reason && <p className="text-amber-700">{slot.degradation_reason.replace(/_/g, ' ')}</p>}
              {(slot.rows || []).map(row => (
                <div key={row.row_id} className="mt-2 rounded border border-gray-100 bg-gray-50 px-2 py-1">
                  <p className="font-medium text-gray-900">
                    Row {row.row_index}: {row.tag?.label || labelize(row.tag?.value)}
                  </p>
                  {row.selected_card && (
                    <p className="text-gray-500">{row.selected_card.card_title}</p>
                  )}
                  {rowFallbackCopy(row) && (
                    <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                      {rowFallbackCopy(row)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}

function LeadModal({ lead, onClose, onSave, onDelete, onRunPipeline, onPreviewComposer }) {
  const [form, setForm] = useState({
    request_status: lead.request_status || '',
    admin_notes: lead.admin_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [composerResult, setComposerResult] = useState(null);
  const [error, setError] = useState('');

  // Keep form in sync if lead updates from polling
  useEffect(() => {
    setForm(f => ({
      ...f,
      request_status: lead.request_status || '',
      admin_notes: lead.admin_notes || '',
    }));
  }, [lead.request_status, lead.admin_notes]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(lead.id, form);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runPipeline = async () => {
    setRunning(true);
    setError('');
    try {
      await onRunPipeline(lead.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const previewPlan = async () => {
    setPreviewing(true);
    setError('');
    try {
      setComposerResult(await onPreviewComposer(lead.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const dates = lead.trip_dates || {};

  return (
    <DraggableModal isOpen onClose={onClose} title={lead.name || lead.email} initialWidth={900} initialHeight={620}>
      <div className="flex h-full overflow-hidden">
        {/* Left: info panel */}
        <div className="w-80 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto p-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Contact</p>
            <Row label="Name" value={lead.name} />
            <Row label="Email" value={lead.email} />
            <Row label="Completion" value={lead.completion_status} />
            <Row label="Started" value={fmtDate(lead.started_at)} />
            <Row label="Completed" value={fmtDate(lead.completed_at)} />
            <Row label="Abandon email" value={fmtDate(lead.abandon_email_sent_at)} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Trip</p>
            <Row label="Area" value={lead.area} />
            <Row label="Arrival" value={dates.arrival} />
            <Row label="Departure" value={dates.departure} />
            <Row label="Accommodation" value={lead.accommodation} />
            <Row label="Group type" value={lead.group_type} />
            <Row label="Group notes" value={lead.group_type_notes} />
            <Row label="Kids ages" value={lead.kids_ages} />
            <Row label="Visit reason" value={Array.isArray(lead.visit_reason) ? lead.visit_reason.join(', ') : lead.visit_reason} />
            <Row label="Trip ranking" value={lead.trip_type_ranked} />
            <Row label="Weather" value={lead.weather_handling} />
            <Row label="Mobility" value={lead.mobility} />
            <Row label="Mobility notes" value={lead.mobility_notes} />
            <Row label="Pace" value={lead.pace} />
            <Row label="Gear" value={lead.gear} />
            <Row label="Gear bringing" value={lead.gear_bringing} />
            <Row label="Gear capabilities" value={lead.derived_gear_capabilities} />
            <Row label="Rental obligations" value={lead.rental_obligations} />
            <Row label="Motorboat access" value={lead.motorboat_access} />
            <Row label="Bringing dog" value={lead.bringing_dog == null ? null : (lead.bringing_dog ? 'Yes' : 'No')} />
            <Row label="Pet constraints" value={lead.pet_constraints} />
            <Row label="Budget" value={lead.budget} />
            <Row label="Budget notes" value={lead.budget_notes} />
            <Row label="Win condition" value={lead.win_condition} />
          </div>
          {lead.preferences_text && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Preferences</p>
              <p className="text-xs text-gray-700 whitespace-pre-wrap">{lead.preferences_text}</p>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Pipeline</p>
            <Row label="Status" value={lead.pipeline_status} />
            <Row label="Run package" value={lead.run_package_id} />
          </div>
        </div>

        {/* Right: edit panel */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <ErrorAlert error={error} onClose={() => setError('')} />}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Request status</label>
            <div className="flex flex-wrap gap-2">
              {['pending', 'in_progress', 'completed', 'cancelled'].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, request_status: s }))}
                  className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-all ${
                    form.request_status === s
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-slate-600'
                  }`}
                >
                  {s.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Admin notes</label>
            <textarea
              rows={5}
              value={form.admin_notes}
              onChange={e => setForm(f => ({ ...f, admin_notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Internal notes…"
            />
          </div>

          {/* Pipeline action */}
          {lead.completion_status === 'completed' && (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                onClick={runPipeline}
                disabled={running || lead.pipeline_status === 'queued' || lead.pipeline_status === 'running'}
                className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-800 disabled:opacity-50 transition-colors"
              >
                <Play className="w-4 h-4" />
                {running ? 'Queuing…' : 'Run Pipeline'}
              </button>
              <button
                onClick={previewPlan}
                disabled={previewing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
              >
                <Route className="w-4 h-4" />
                {previewing ? 'Composing...' : 'Preview Plan'}
              </button>
              <PipelineBadge status={lead.pipeline_status} />
            </div>
          )}

          <LeadPlanPreview result={composerResult} />

          {/* Footer buttons */}
          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <ActionButton variant="primary" onClick={save} loading={saving}>Save</ActionButton>
            <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
            <ActionButton
              variant="danger"
              icon={Trash2}
              onClick={() => { if (window.confirm('Delete this lead?')) onDelete(lead.id); }}
              className="ml-auto"
            >
              Delete
            </ActionButton>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}

// ── Leads View ──────────────────────────────────────────────────────────────
export default function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.getLeads();
      setLeads(data);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 10s live poll while a lead is selected
  useEffect(() => {
    if (!selectedId) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [selectedId, load]);

  const handleSave = async (id, body) => {
    const updated = await api.patchLead(id, body);
    setLeads(ls => ls.map(l => (l.id === id ? { ...l, ...updated } : l)));
  };

  const handleDelete = async (id) => {
    await api.deleteLead(id);
    setLeads(ls => ls.filter(l => l.id !== id));
    setSelectedId(null);
  };

  const handleRunPipeline = async (id) => {
    await api.runPipeline(id);
    await load();
  };

  const handlePreviewComposer = async (id) => {
    return api.composerPreview(id);
  };

  const handleSendFollowups = async () => {
    setSending(true);
    try {
      const res = await api.sendAbandonedFollowups();
      alert(`Sent: ${res.sent ?? 0} emails`);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.name || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q) || (l.area || '').toLowerCase().includes(q);
  });

  const selected = leads.find(l => l.id === selectedId) ?? null;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Guest Leads</h1>
          <p className="text-sm text-gray-500">{leads.length} total</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSendFollowups}
            disabled={sending}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send followups'}
          </button>
          <button onClick={load} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {error && <ErrorAlert error={error} />}

      {/* Search */}
      <input
        type="text"
        placeholder="Search name, email, area…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Area</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Pipeline</th>
              <th className="px-4 py-3 text-left font-medium">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(l => (
              <tr
                key={l.id}
                onClick={() => setSelectedId(l.id)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 font-medium text-gray-900">{l.name || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{l.email}</td>
                <td className="px-4 py-3 text-gray-600">{l.area || '—'}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={l.completion_status} variant="pill" />
                </td>
                <td className="px-4 py-3">
                  <PipelineBadge status={l.pipeline_status} />
                </td>
                <td className="px-4 py-3 text-gray-500">{fmtDate(l.completed_at || l.started_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center py-10 text-gray-400 text-sm">No leads found</p>
        )}
      </div>

      {selected && (
        <LeadModal
          lead={selected}
          onClose={() => setSelectedId(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          onRunPipeline={handleRunPipeline}
          onPreviewComposer={handlePreviewComposer}
        />
      )}
    </div>
  );
}
